
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { adminPrivilegedService, AdminUsageMetrics } from '@/services/admin/adminPrivilegedService';
import { TrendingUp, Users, ChefHat, Calendar, Home, BarChart3, UserPlus, Target } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

const UsageMetricsDisplay: React.FC = () => {
  const [metrics, setMetrics] = useState<AdminUsageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminPrivilegedService.getUsageMetrics();
        setMetrics(data);
      } catch (err) {
        console.error('Failed to load usage metrics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const getGrowthBadge = (growth: number) => {
    if (growth === 0) return null;
    const variant = growth > 0 ? 'default' : 'destructive';
    const sign = growth > 0 ? '+' : '';
    return (
      <Badge variant={variant} className="ml-2 text-xs">
        {sign}{growth}%
      </Badge>
    );
  };

  if (loading) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6 mt-2">Usage Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-3 w-20 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6 mt-2">Usage Metrics</h2>
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 font-medium">Error loading metrics</p>
                <p className="text-sm text-red-500 mt-1">{error}</p>
              </div>
              <Badge variant="destructive">Error</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!metrics) return null;

  const metricCards = [
    {
      title: 'Active Users (7 days)',
      value: metrics.activeUsersLast7Days,
      icon: Users,
      color: 'text-green-600',
      description: 'Users with activity this week',
      growth: null
    },
    {
      title: 'Active Users (30 days)',
      value: metrics.activeUsersLast30Days,
      icon: TrendingUp,
      color: 'text-blue-600',
      description: 'Users with activity this month',
      growth: null
    },
    {
      title: 'User Engagement Rate',
      value: `${metrics.userEngagementRate}%`,
      icon: Target,
      color: 'text-purple-600',
      description: 'Active users / total users',
      growth: null
    },
    {
      title: 'Registration Growth',
      value: `${metrics.userRegistrationGrowthRate}%`,
      icon: UserPlus,
      color: 'text-indigo-600',
      description: 'New registrations vs last 30 days',
      growth: metrics.userRegistrationGrowthRate
    },
    {
      title: 'Recipes per User',
      value: metrics.recipesPerUser,
      icon: ChefHat,
      color: 'text-orange-600',
      description: 'Average recipes created per user',
      growth: null
    },
    {
      title: 'Meal Plans per User',
      value: metrics.mealPlansPerUser,
      icon: Calendar,
      color: 'text-pink-600',
      description: 'Average meal plans created per user',
      growth: null
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold mt-2">Usage Metrics</h2>
        <Badge variant="default" className="bg-green-100 text-green-800">
          Live Data
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metricCards.map((card, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {card.title}
              </CardTitle>
              <card.icon className={`h-4 w-4 ${card.color}`} />
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <div className="text-2xl font-bold">{typeof card.value === 'string' ? card.value : card.value.toLocaleString()}</div>
                {card.growth !== null && getGrowthBadge(card.growth)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {card.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default UsageMetricsDisplay;
